import { useRef } from "react";
import clsx from "clsx";
import useProductDetailPage from "@/viewModels/product-detail/useProductDetailPage";
import { stripHTML } from "@/utils/utils";
import { useBreakpointContext } from "@/contexts/BreakpointContext";
import useBottomSheet from "@/components/ui/bottomSheet/useBottomSheet";
import useScrollVisibility from "@/hooks/useScrollVisibility";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import Tabs from "@/components/ui/Tabs";
import BottomSheet from "@/components/ui/bottomSheet";
import AddToCartForm from "@/features/addToCart/components/AddToCartForm";
import ProductPrice from "@/components/features/products/ProductPrice";
import ProductImageSlider from "./components/ProductImageSlider";
import RelatedProductSlider from "@/pages/product-detail/components/RelatedProductSlider";
import AddToCartBtn from "@/components/features/products/AddToCartBtn";
import SEOMeta from "@/components/ui/meta/SEOMeta";

const ProductDetailPage = () => {
  const {
    product: { product, isPurchasable, isProductLoading, isNewProduct },

    breadcrumbs: { breadcrumbs },

    variations: {
      selectedTerms,
      availableTermsSetMap,
      isVariationsLoading,
      displayPrices,
      handleAttributeSelect,
    },
    quantity: { quantity, maxQuantity, handleQuantityChange },
    cart: { isAddToCartLoading, handleAddToCart, handleQuickAddToCartClick },

    relatedProducts,
  } = useProductDetailPage();

  // #region UI Logic
  const { isDesktop } = useBreakpointContext();
  const quickAddToCartSheet = useBottomSheet();

  const addToCartFormRef = useRef<HTMLDivElement>(null);
  const showFixedBtn = useScrollVisibility(addToCartFormRef, {
    enabled: !isDesktop && !!product,
  });
  // #endregion

  if (!product || isProductLoading) {
    return <ProductDetailSkeleton />;
  }

  const { name, short_description, description, images } = product;

  return (
    <>
      <div className="brand-container pb-16">
        <section className="lg:grid lg:grid-cols-2 lg:gap-12">
          {/* Images */}
          <div className="relative lg:mt-6">
            {isNewProduct && (
              <span className="absolute right-2 top-2 z-20 rounded bg-accent px-2 py-1 text-xs text-white opacity-80">
                新品
              </span>
            )}
            {!isPurchasable && (
              <span className="absolute right-2 top-2 z-20 rounded bg-black px-2 py-1 text-xs font-normal text-white opacity-50">
                售完
              </span>
            )}
            <ProductImageSlider images={images} name={name} />
          </div>

          <div className="mt-4 lg:mt-4">
            {/* Breadcrumbs */}
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            {/* Name */}
            <h1 className="brand-heading-md mt-2 flex flex-wrap items-center gap-2 text-primary">
              {name}
              {!isPurchasable && (
                <span className="rounded bg-black px-2 py-1 text-xs font-normal text-white opacity-50">
                  售完
                </span>
              )}
            </h1>
            {/* Price */}
            <div className="mt-2">
              <ProductPrice
                price={displayPrices.price}
                regular_price={displayPrices.regular_price}
                on_sale={displayPrices.on_sale}
                price_range={displayPrices.price_range}
                size="lg"
              />
            </div>

            {/* Short Description */}
            {short_description && (
              <div className="mt-6">
                <div className="brand-text-sm font-bold text-base-content">風味參考</div>
                <p className="brand-text-sm mt-2 leading-relaxed text-base-content">
                  {short_description}
                </p>
              </div>
            )}

            <div ref={addToCartFormRef}>
              <AddToCartForm
                product={product}
                isPurchasable={isPurchasable}
                displayPrices={displayPrices}
                selectedTerms={selectedTerms}
                availableTermsSetMap={availableTermsSetMap}
                isVariationsLoading={isVariationsLoading}
                handleAttributeSelect={handleAttributeSelect}
                quantity={quantity}
                maxQuantity={maxQuantity}
                handleQuantityChange={handleQuantityChange}
                isAddToCartLoading={isAddToCartLoading}
                handleAddToCart={handleAddToCart}
              />
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="mb-16 mt-12 lg:mt-16">
          <Tabs
            data={[
              { title: "商品介紹", content: description },
              {
                title: "購買須知",
                content: (
                  <ul className="list-disc space-y-2 leading-relaxed">
                    <li>
                      咖啡豆採「新鮮烘焙」，每週五晚上 12:00 結單統計，於週六 / 週日進行烘焙。
                    </li>
                    <li>烘焙完成後，將於週一 ~ 週三安排出貨，約 2～3 個工作天可寄達。</li>
                    <li>標註「現貨」的商品，為近期烘焙好的咖啡，下單後 2 天內寄出。</li>
                  </ul>
                ),
              },
            ]}
            tabsClass="px-0 lg:px-28 shadow-[inset_0_-1px_0_rgba(84,84,84,0.2)]"
            contentClass="m-auto max-w-3xl"
          />
        </section>

        {/* Related Product: Category & Roast Attribute */}
        <RelatedProductSlider {...relatedProducts} />
      </div>

      {/* Fixed Add to Cart Btn */}
      {!isDesktop && (
        <div
          className={clsx(
            "brand-fixed-panel z-fixedButton",
            "transition-transform duration-300 ease-in-out",
            showFixedBtn ? "translate-y-0" : "translate-y-full",
          )}
        >
          <div className="brand-container w-full max-w-screen-md">
            <AddToCartBtn
              disabled={isAddToCartLoading || !isPurchasable}
              isLoading={isAddToCartLoading}
              isPurchasable={isPurchasable}
              handleClick={() => handleQuickAddToCartClick(quickAddToCartSheet.handleOpen)}
            />
          </div>
        </div>
      )}

      {/* BottomSheet */}
      <BottomSheet
        show={quickAddToCartSheet.isOpen}
        onClose={quickAddToCartSheet.handleClose}
        title="請選規格"
      >
        {/* Name */}
        <h1 className="brand-heading-md text-primary">{name}</h1>
        {/* Price */}
        <div className="mt-2">
          <ProductPrice
            price={displayPrices.price}
            regular_price={displayPrices.regular_price}
            on_sale={displayPrices.on_sale}
            price_range={displayPrices.price_range}
            size="lg"
          />
        </div>
        <AddToCartForm
          product={product}
          isPurchasable={isPurchasable}
          displayPrices={displayPrices}
          selectedTerms={selectedTerms}
          availableTermsSetMap={availableTermsSetMap}
          handleAttributeSelect={handleAttributeSelect}
          quantity={quantity}
          maxQuantity={maxQuantity}
          handleQuantityChange={handleQuantityChange}
          isAddToCartLoading={isAddToCartLoading}
          handleAddToCart={handleAddToCart}
          closeBottomSheet={quickAddToCartSheet.handleClose}
        />
      </BottomSheet>

      {/* Meta Tag */}
      <SEOMeta
        pageTitle={name}
        description={
          stripHTML(short_description || "") || "探索更多關於此商品的資訊，了解其風味與特色。"
        }
      />
    </>
  );
};

const ProductDetailSkeleton = () => (
  <div className="brand-container animate-pulse pb-12">
    <div className="lg:grid lg:grid-cols-2 lg:gap-12">
      <div className="flex flex-col gap-2">
        <div className="brand-product-placeholder w-full" />
        <div className="flex gap-2">
          <div className="brand-product-placeholder w-32" />
          <div className="brand-product-placeholder w-32" />
        </div>
      </div>
      <div className="mt-4 *:bg-base-300">
        <div className="mt-2 h-4 w-1/3" />
        <div className="mt-6 h-8 w-1/2" />
        <div className="mt-2 h-8 w-1/3" />
        <div className="mt-8 h-4 w-12" />
        <div className="mt-2 h-10 w-1/2" />
        <div className="mt-8 h-4 w-12" />
        <div className="mt-2 h-10 w-2/5" />
        <div className="mt-8 h-4 w-12" />
        <div className="mt-2 h-10 w-36" />
        <div className="mt-8 h-10 w-1/2" />
      </div>
    </div>
  </div>
);

export default ProductDetailPage;
